import { z } from 'zod';

const text = z.string().min(1);
const minutes = z.number().int();

/**
 * The facts of a flight alert or reminder that the e-mail's flight card lays out (#7099), as the
 * cirium stamps them on the `flight_disruption`/`flight_reminder` metadata (`flightIdentity`,
 * `changeMetadata`, `flightCardFields`, the policy's connection fields). Language-neutral: IATA
 * codes and offset-less airport-local wall clocks; the e-mail names the cities and formats the
 * hours. The conversation ships them as the unpositioned `flightJson` var, so WhatsApp, which
 * reads only the positioned vars, never changes. Every field but the type is optional: a fact
 * the alert does not carry drops its piece of the card.
 */
export const FlightAlertFactsSchema = z.object({
  alertType: text,
  flightCarrier: text.optional(),
  flightNumber: text.optional(),
  flightFrom: text.optional(),
  flightTo: text.optional(),
  /** The departure the traveler was sold, and the new one (a delay's estimate, a retime). */
  scheduledLocal: text.optional(),
  estimatedLocal: text.optional(),
  newTimeLocal: text.optional(),
  timeSide: text.optional(),
  scheduledArrivalLocal: text.optional(),
  estimatedArrivalLocal: text.optional(),
  delayMinutes: minutes.optional(),
  delaySide: text.optional(),
  durationMinutes: minutes.positive().optional(),
  stops: minutes.min(0).optional(),
  gateTo: text.optional(),
  gateFrom: text.optional(),
  gateSide: text.optional(),
  terminalTo: text.optional(),
  terminalSide: text.optional(),
  baggageClaim: text.optional(),
  newAirport: text.optional(),
  airlineLocator: text.optional(),
  finalDestination: text.optional(),
  connectionAirport: text.optional(),
  connectionArrivalLocal: text.optional(),
  connectionNextDepartureLocal: text.optional(),
  connectionTimeMinutes: minutes.optional(),
  nextFlightCarrier: text.optional(),
  nextFlightNumber: text.optional(),
  nextFlightTo: text.optional(),
  nextDepartureLocal: text.optional(),
  /** The alert's own instant on the departure airport's clock ("hoje" vs a date). */
  alertLocal: text.optional(),
  /** The requester's copy (`recipientRole: 'requester'`) names whose flight it is. */
  travelerName: text.optional(),
});
export type FlightAlertFacts = z.infer<typeof FlightAlertFactsSchema>;

/**
 * The facts off the loose alert metadata: the known keys, each kept only when it has the
 * expected shape (a wrong one drops that fact, never the e-mail). Null without an alert type.
 */
export function flightAlertFactsOf(metadata: Record<string, unknown>): FlightAlertFacts | null {
  const shape = FlightAlertFactsSchema.shape;
  const facts: Record<string, unknown> = {};
  for (const key of Object.keys(shape) as (keyof typeof shape)[]) {
    if (shape[key].safeParse(metadata[key]).success && metadata[key] !== undefined) {
      facts[key] = metadata[key];
    }
  }
  const parsed = FlightAlertFactsSchema.safeParse(facts);
  return parsed.success ? parsed.data : null;
}

/** Parses the `flightJson` var; null when absent or unreadable (→ the e-mail of today). */
export function parseFlightAlertFacts(raw: string | undefined): FlightAlertFacts | null {
  if (raw === undefined) return null;
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return null;
  }
  const parsed = FlightAlertFactsSchema.safeParse(json);
  return parsed.success ? parsed.data : null;
}
